import { useState, useEffect, useCallback } from 'react';
import { Canvas, FabricObject } from 'fabric';

export interface LargestImageResult {
  object: FabricObject;
  mediaType: 'photo' | 'object';
}

/**
 * Finds the largest content image on the canvas.
 * Excludes metadata/service objects by category.
 *
 * Note: content images (photos, objects) often have layer='background' but
 * category='crop' - they are still valid user-facing images.
 * We exclude only truly non-visual categories: 'backgrounds' (available_images container),
 * 'background' (background color/image metadata), 'metadata', 'missing_items', 'icons'.
 */
export function useLargestCanvasImage(canvas: Canvas | null): LargestImageResult | null {
  const [result, setResult] = useState<LargestImageResult | null>(null);

  const findLargestImage = useCallback(() => {
    if (!canvas) {
      setResult(null);
      return;
    }

    const excludedCategories = ['backgrounds', 'background', 'metadata', 'missing_items', 'icons'];

    const objects = canvas.getObjects();
    const imageObjects = objects.filter((obj) => {
      if (!obj.type || obj.type.toLowerCase() !== 'image') return false;
      // Only photo and object media types
      const mediaType = obj.media_type?.toLowerCase();
      if (mediaType !== 'photo' && mediaType !== 'object') return false;
      // Exclude markers
      if (obj.type === 'marker' && obj.isMarker === true) return false;
      // Exclude marker content
      if (obj.isMarkerContent === true || obj.markerId) return false;
      // Exclude service/metadata objects by category
      const cat = obj.category?.toLowerCase();
      if (cat && excludedCategories.includes(cat)) return false;
      // Exclude bullet image items
      if (cat === 'bullet-image-item') return false;
      return true;
    });

    if (imageObjects.length === 0) {
      setResult(null);
      return;
    }

    // Sort by area descending
    const sorted = [...imageObjects].sort((a, b) => {
      const areaA = (a.width || 0) * (a.scaleX || 1) * ((a.height || 0) * (a.scaleY || 1));
      const areaB = (b.width || 0) * (b.scaleX || 1) * ((b.height || 0) * (b.scaleY || 1));
      return areaB - areaA;
    });

    const largest = sorted[0];
    const mediaType = largest.media_type === 'object' ? 'object' : 'photo';

    setResult({ object: largest, mediaType });
  }, [canvas]);

  useEffect(() => {
    findLargestImage();

    if (!canvas) return;

    // Re-calculate when canvas objects change
    const handleChange = () => findLargestImage();
    canvas.on('object:added', handleChange);
    canvas.on('object:removed', handleChange);

    return () => {
      canvas.off('object:added', handleChange);
      canvas.off('object:removed', handleChange);
    };
  }, [canvas, findLargestImage]);

  return result;
}
